import { extendObservable, action, observe, toJS } from 'mobx';
import moment from 'moment';
import 'moment-duration-format';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import omitBy from 'lodash/omitBy';
import pickBy from 'lodash/pickBy';
import FetchService from '../../../core/services/FetchService';
import FormService from '../../../core/services/FormService';
import NotificationService from '../../../core/services/NotificationService';
import jsonFormData from 'json-form-data';
import {
  isRequired,
  minLength,
  minFilteredLength,
  onlyOneCorrectAnswer,
  isValidTime,
  exactLength,
  maxLength,
  isValidAudioDuration,
  isValidImageSize,
} from '../../../core/validations';
import VideoUploadService from '../../../core/services/VideoUploadService';

const ISO_DATE_PATTERN = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/;

const sanitizeDictionary = (dictionary) =>
  Object.keys(dictionary || {}).reduce((result, key) => {
    const value = dictionary[key] && dictionary[key].trim();
    return value ? { ...result, [key]: value } : result;
  }, {});

const sanitizeText = (text) =>
  text && typeof text === 'string'
    ? text.trim().replace(new RegExp(String.fromCharCode(8217), 'g'), String.fromCharCode(39))
    : text;

const sanitizeGapFillDictationGaps = (gaps) =>
  gaps &&
  gaps.map((gap) => ({
    ...gap,
    text: sanitizeText(gap.text),
    acceptedAnswers: (gap.acceptedAnswers || []).map((answer) => {
      const speechRecognitionDictionary = sanitizeDictionary(answer.speechRecognitionDictionary);
      return {
        ...omit(answer, ['speechRecognitionDictionary']),
        text: sanitizeText(answer.text),
        ...(Object.keys(speechRecognitionDictionary).length && { speechRecognitionDictionary }),
      };
    }),
  }));

export default class ItemFormService {
  fetch = new FetchService();
  submit = new FetchService();
  form = new FormService();

  defaultValidations = {
    'item.type': [isRequired],
    'item.time': [(value) => !value && 'Time is required'],
    'item.grammar': [isRequired],
  };

  constructor(endpointUrl, order, onSaveSuccess, isTestItem) {
    extendObservable(this, {
      itemId: null,
    });
    this.isTestItem = isTestItem;
    this.onSaveSuccess = onSaveSuccess;
    this.endpointUrl = endpointUrl;
    this.order = order;
    this.form.validations = {
      ...this.defaultValidations,
    };
  }

  createVideoTimeObserver() {
    observe(this.form.values.item, 'videoStartTime', () => {
      const { item } = this.form.getValues();
      if (item.videoStartTime && item.videoEndTime) {
        const start = moment(item.videoStartTime, 'hhmmss');
        const end = moment(item.videoEndTime, 'hhmmss');

        this.form.setValue('item.time', end.diff(start, 'seconds'));
      }
    });
    observe(this.form.values.item, 'videoEndTime', () => {
      const { item } = this.form.getValues();
      if (item.videoStartTime && item.videoEndTime) {
        const start = moment(item.videoStartTime, 'hhmmss');
        const end = moment(item.videoEndTime, 'hhmmss');

        this.form.setValue('item.time', end.diff(start, 'seconds'));
      }
    });
  }

  createTextObserver() {
    if (!this.form.getValues().item.text || !this.form.getValues().item.answers.length) {
      return;
    }

    // function to handle changes on item text and update answers
    observe(this.form.values.item, 'text', () => {
      const { item } = this.form.getValues();
      if (
        [
          'GAP_FILL',
          'GAP_FILL_MULTIPLE',
          'GAP_FILL_SELECT',
          'UNSCRAMBLE_DRAG_AND_DROP',
          'UNSCRAMBLE_SPEECH_RECOGNITION',
        ].find((type) => type === item.type.key)
      ) {
        if (item.answers) {
          const slices = item.text.trim().split(' ');
          item.answers = item.answers.reduce((acc, answer) => {
            if (!answer.correct || (answer.correct && answer.index < slices.length)) {
              if (answer.correct) {
                answer.text = slices[answer.index];
              }
              return [...acc, answer];
            }
            return [...acc];
          }, []);
          this.form.setValue('item.answers', item.answers);
          this.form.setValue(
            'item.indexesToRemove',
            item.answers.filter((slice) => slice.index !== undefined)
          );
        }
      }
    });
  }

  setValidationsByItemType = action(() => {
    const correctAnswerPredicate = (answer) => answer.correct;
    const wrongAnswerPredicate = (answer) => !answer.correct;

    switch (this.form.getValue('item.type').key) {
      case 'VIDEO':
        this.form.validations = {
          ...this.defaultValidations,
          'item.videoStartTime': [isRequired, isValidTime],
          'item.videoEndTime': [isRequired, isValidTime],
        };
        this.createVideoTimeObserver();
        break;
      case 'MUSIC':
        this.form.validations = {
          ...this.defaultValidations,
          'item.videoStartTime': [isRequired, isValidTime],
          'item.videoEndTime': [isRequired, isValidTime],
        };
        this.createVideoTimeObserver();
        break;
      case 'VIDEO_SHORT':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.videoStartTime': [isRequired, isValidTime],
          'item.videoEndTime': [isRequired, isValidTime],
        };
        break;
      case 'VIDEO_LONG':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
        };
        break;
      case 'VIDEO_TEXT_AREA':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.reference': [isRequired],
          'item.videoStartTime': [isRequired, isValidTime],
          'item.videoEndTime': [isRequired, isValidTime],
          'item.indexesToRemove': [isRequired, minLength(10)],
        };
        break;
      case 'SINGLE_CHOICE_TEXT':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.translation': this.isTestItem ? [] : [isRequired],
          'item.answers': [
            onlyOneCorrectAnswer,
            minFilteredLength(1, correctAnswerPredicate, 'Add at least 1 correct answer'),
            minFilteredLength(3, wrongAnswerPredicate, 'Add at least 3 wrong answers'),
          ],
        };
        break;
      case 'SINGLE_CHOICE_IMAGE':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.translation': this.isTestItem ? [] : [isRequired],
          'item.answers': [
            onlyOneCorrectAnswer,
            minFilteredLength(1, correctAnswerPredicate, 'Add at least 1 correct answer'),
            minFilteredLength(3, wrongAnswerPredicate, 'Add at least 3 wrong answers'),
          ],
        };
        break;
      case 'SINGLE_CHOICE_AUDIO':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.translation': this.isTestItem ? [] : [isRequired],
          'item.answers': [
            onlyOneCorrectAnswer,
            minFilteredLength(1, correctAnswerPredicate, 'Add at least 1 correct answer'),
            minFilteredLength(3, wrongAnswerPredicate, 'Add at least 3 wrong answers'),
          ],
        };
        break;
      case 'SINGLE_CHOICE_KIDS':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.translation': this.isTestItem ? [] : [isRequired],
          'item.answers': [
            onlyOneCorrectAnswer,
            // minFilteredLength(1, correctAnswerPredicate, 'Add at least 1 correct answer'),
            minFilteredLength(1, wrongAnswerPredicate, 'Add at least 1 wrong answers'),
          ],
        };
        break;
      case 'SINGLE_CHOICE_GAME':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.answers': [
            onlyOneCorrectAnswer,
            minFilteredLength(1, wrongAnswerPredicate, 'Add at least 1 wrong answers'),
            exactLength(3),
          ],
        };
        break;
      case 'MULTIPLE_CHOICE_TEXT':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.translation': this.isTestItem ? [] : [isRequired],
          'item.answers': [
            minLength(4),
            maxLength(5),
            minFilteredLength(1, correctAnswerPredicate, 'Add at least 1 correct answers'),
            (value) =>
              value.length === 4 &&
              value.filter((v) => v.correct).length > 2 &&
              'This item needs a minimum of one correct answer, a maximum of two correct answers when the exercise has four answers and a maximum of three correct answers when the exercise has five answers',
            (value) =>
              value.length === 5 &&
              value.filter((v) => v.correct).length > 3 &&
              'This item needs a minimum of one correct answer, a maximum of two correct answers when the exercise has four answers and a maximum of three correct answers when the exercise has five answers',
          ],
        };
        break;
      case 'DICTATION':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.translation': this.isTestItem ? [] : [isRequired],
        };
        break;
      case 'GAP_FILL':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.translation': this.isTestItem ? [] : [isRequired],
          'item.indexesToRemove': [isRequired, minLength(1)],
          'item.answers': [
            onlyOneCorrectAnswer,
            minFilteredLength(3, wrongAnswerPredicate, 'Add at least 3 wrong answers'),
          ],
        };
        break;
      case 'GAP_FILL_IMAGE':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.translation': this.isTestItem ? [] : [isRequired],
          'item.indexesToRemove': [isRequired, minLength(1)],
          'item.answers': [
            onlyOneCorrectAnswer,
            minFilteredLength(1, wrongAnswerPredicate, 'Add at least 1 wrong answers'),
          ],
        };
        break;
      case 'GAP_FILL_SELECT':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.translation': this.isTestItem ? [] : [isRequired],
          'item.indexesToRemove': [isRequired, minLength(1)],
          'item.answers': [minFilteredLength(1, wrongAnswerPredicate, 'Add at least 1 wrong answers')],
        };
        break;
      case 'GAP_FILL_MULTIPLE':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.translation': this.isTestItem ? [] : [isRequired],
          'item.indexesToRemove': [isRequired, minLength(2)],
          'item.answers': [minFilteredLength(1, wrongAnswerPredicate, 'Add at least 1 wrong answers')],
        };
        break;
      case 'GAP_FILL_DICTATION':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.spellCheckStatus': [
            (value, values) =>
              (value !== 'OK' || values.item.spellCheckedText !== values.item.text) &&
              'Run and approve Spell Check before saving.',
          ],
          'item.indexesToRemove': [isRequired, minLength(1)],
          'item.gaps': [
            isRequired,
            minLength(1),
            (gaps) =>
              gaps &&
              gaps.some((gap) => !gap.acceptedAnswers || !gap.acceptedAnswers.length) &&
              'Every gap must have at least one accepted answer',
            (gaps) => {
              if (!gaps || !gaps.length) {
                return null;
              }
              const orderedGaps = [...gaps].sort((a, b) => a.indexes[0] - b.indexes[0]);
              return (
                orderedGaps.some((gap, index) => {
                  const nextGap = orderedGaps[index + 1];
                  return nextGap && gap.indexes[gap.indexes.length - 1] + 1 === nextGap.indexes[0];
                }) && 'Consecutive gaps must be linked into a single gap'
              );
            },
          ],
        };
        break;
      case 'GAP_FILL_LETTER':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.translation': this.isTestItem ? [] : [isRequired],
          'item.indexesToRemove': [isRequired, minLength(1)],
          'item.answers': [
            onlyOneCorrectAnswer,
            minFilteredLength(2, wrongAnswerPredicate, 'Add at least 2 wrong letters'),
          ],
        };
        break;
      case 'PRESENTATION':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.translation': this.isTestItem ? [] : [isRequired],
          // audio: [isRequired],
          // image: [isRequired],
        };
        break;
      case 'PRONUNCIATION':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.translation': this.isTestItem ? [] : [isRequired],
        };
        break;
      case 'SPEECH_PRACTICE':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.translation': this.isTestItem ? [] : [isRequired],
        };
        break;
      case 'TEXT':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
        };
        break;
      case 'TRUE_FALSE':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.translation': this.isTestItem ? [] : [isRequired],
          'item.answers': [onlyOneCorrectAnswer, isRequired, exactLength(2)],
        };
        break;
      case 'TRUE_FALSE_KIDS':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.translation': this.isTestItem ? [] : [isRequired],
          'item.answers': [onlyOneCorrectAnswer, isRequired, exactLength(2)],
        };
        break;
      case 'UNSCRAMBLE_DRAG_AND_DROP':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.translation': this.isTestItem ? [] : [isRequired],
          'item.indexesToRemove': [isRequired, minLength(3)],
          'item.answers': [minFilteredLength(1, wrongAnswerPredicate, 'Add at least 1 wrong answers')],
        };
        break;
      case 'UNSCRAMBLE_SPEECH_RECOGNITION':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.translation': this.isTestItem ? [] : [isRequired],
          'item.indexesToRemove': [isRequired],
          'item.answers': [
            (value) => value && value.filter((answer) => !answer.linkTo).length > 4 && 'The maximum of gaps is 4',
          ],
        };
        break;
      case 'UNSCRAMBLE_TEXT':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [
            isRequired,
            (value) =>
              value && value.split('\n').length < 3 && 'The text of this item must have a minimum of 3 paragraphs.',
          ],
        };
        break;
      case 'DIALOGUE':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.dialogueCharacter': [isRequired],
        };
        break;
      case 'DIALOGUE_OPTION':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.answers': [isRequired, exactLength(2)],
          'item.dialogueCharacter': [isRequired],
        };
        break;
      case 'FREE_TEXT':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.charactersMinLimit': [isRequired],
          'item.charactersMaxLimit': [isRequired],
        };
        break;
      case 'FREE_TEXT_IMAGE':
        this.form.validations = {
          ...this.defaultValidations,
          'item.image': [isRequired],
          'item.charactersMinLimit': [isRequired],
          'item.charactersMaxLimit': [isRequired],
        };
        break;
      case 'FREE_SPEAK':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
        };
        break;
      case 'FREE_SPEAK_IMAGE':
        this.form.validations = {
          ...this.defaultValidations,
          'item.image': [isRequired],
        };
        break;
      case 'CONNECTING_DOTS':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.postPhrase': [isRequired],
        };
        break;
      case 'MEMORY_GAME':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.postPhrase': [isRequired],
        };
        break;
      case 'VOCABULARY_GAME':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.postPhrase': [isRequired],
        };
        break;
      case 'VOCABULARY':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.postPhrase': [isRequired],
        };
        break;
      case 'VOCABULARY_ACADEMIC':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.postPhrase': [isRequired],
        };
        break;
      case 'PHONEME':
        this.form.validations = {
          ...this.defaultValidations,
          'item.title': [isRequired],
          'item.text': [isRequired],
          'item.postPhrase': [isRequired],
        };
        break;
      case 'GRAMMAR_CHECK':
        this.form.validations = {
          ...this.defaultValidations,
          'item.text': [isRequired],
          'item.indexesToRemove': [isRequired, minLength(1)],
          'item.answers': [isRequired, minLength(1)],
        };
        break;
      case 'PRE_ESSAY':
        this.form.validations = {
          ...this.defaultValidations,
          'item.title': [isRequired],
          'item.text': [isRequired],
          'item.translation': [isRequired],
        };
        break;
      case 'ESSAY':
        this.form.validations = {
          ...this.defaultValidations,
          'item.charactersMinLimit': [isRequired],
        };
        break;
      default:
        break;
    }

    if (!this.form.getValue('item.id')) {
      this.form.setInitialValues({
        item: this.form.getValue('item'),
      });
    }
    // this.form.reset();
  });

  handleChange = async (path, value) => {
    if (path === 'item.video') {
      this.form.setValue('item.videoLink', null);
      this.form.setValue('item.videoExternalLink', null);
      this.form.setValue('item.videoFileSize', value && value.size);
    }
    if (path === 'item.type' && this.form.getValue('item.id')) {
      const itemId = this.form.getValue('item.id');
      const oldItemType = this.form.getValue('item.type.key');
      const wasSliceRequired = this.form.validations['item.indexesToRemove'];

      this.form.setValue(path, value);
      this.setValidationsByItemType();
      const isSliceRequired = this.form.validations['item.indexesToRemove'];

      this.form.setValues(
        pick(this.form.getValues(), [
          ...Object.keys(this.form.validations),
          'item.text',
          'item.translation',
          'item.spanishTranslation',
          'item.audio',
          'item.image',
          'item.postPhraseAudio',
          'item.postPhraseImage',
        ])
      );
      this.form.setValue('item.id', itemId);

      if ((oldItemType === 'TRUE_FALSE' || value.key === 'TRUE_FALSE') && oldItemType !== value.key) {
        this.form.setValue('item.answers', []);
      }
      if (wasSliceRequired || isSliceRequired) {
        this.form.setValue('item.answers', []);
      }
      if (oldItemType === 'GAP_FILL_DICTATION' || value.key === 'GAP_FILL_DICTATION') {
        this.form.setValue('item.gaps', []);
        this.form.setValue('item.indexesToRemove', []);
        this.form.setValue('item.answers', []);
        this.form.setValue('item.speechRecognitionDictionary', undefined);
        this.form.setValue('item.spellCheckStatus', '');
        this.form.setValue('item.spellCheckedText', null);
      }
    }
    if (
      path === 'item.audio' &&
      value &&
      ['GAP_FILL', 'PRONUNCIATION', 'SPEECH_PRACTICE', 'PRESENTATION', 'UNSCRAMBLE_SPEECH_RECOGNITION'].includes(
        this.form.getValue('item.type.key')
      )
    ) {
      const valid = await isValidAudioDuration(value);
      if (!valid) {
        NotificationService.addNotification(
          'Item Types Gap Fill, Pronunciation, Speech Practice, Presentation e Unscramble Speech Recognition only accepted mp3 audios less than 30 seconds.',
          'error'
        );
        return;
      }
    }
    if (path === 'item.image' && value && !isValidImageSize(value)) {
      NotificationService.addNotification('Only images smaller than 1MB can be uploaded.', 'error');
      return;
    }
    this.form.setValue(path, value);
  };

  handleLoad = action((itemId, defaultGrammar, copyFrom) => {
    if (itemId) {
      this.fetch
        .fetch({
          url: `/${this.endpointUrl}/${itemId}`,
        })
        .then(() => {
          if (this.fetch.data) {
            const item = this.fetch.data.item || this.fetch.data; //depending on the value in endpointUrl, the item comes at the root of the data and not inside an item property
            this.form.setInitialValues({
              ...this.fetch.data,
              'item.indexesToRemove': this.fetch.data, // to validation works
              item: {
                ...omit(item, ['audioWaveform']),
                video: item.videoExternalLink || item.videoLink,
                ...(item.videoStartTime && {
                  videoStartTime: item.videoStartTime.length === 4 ? `00${item.videoStartTime}` : item.videoStartTime,
                }),
                ...(item.videoEndTime && {
                  videoEndTime: item.videoEndTime.length === 4 ? `00${item.videoEndTime}` : item.videoEndTime,
                }),
                ...(item.videoSubtitles &&
                  item.videoSubtitles.find((s) => s.locale === 'en') && {
                    englishSubtitle: item.videoSubtitles.find((s) => s.locale === 'en').subtitleUrl,
                  }),
              },
            });
            this.setValidationsByItemType();
            this.createTextObserver();
          }
        });
    } else {
      this.form.setInitialValues(
        copyFrom
          ? {
              item: {
                ...omit(copyFrom, 'id'),
              },
            }
          : {
              item: {
                text: '',
                videoStartTime: '',
                videoEndTime: '',
                ...(defaultGrammar && {
                  grammar: {
                    id: defaultGrammar,
                  },
                }),
              },
            }
      );
      setTimeout(() => {
        this.createTextObserver();
      }, 250);
    }
    this.form.reset();
    this.itemId = itemId;
  });

  handleSubmit = action(() => {
    this.form.submitted = true;
    if (this.form.errors) {
      return;
    }
    const itemId = this.form.getValue('item.id');

    const formData = new FormData();

    formData.append(
      'item',
      new Blob(
        [
          JSON.stringify({
            ...omitBy(
              omit(this.form.getValue('item'), [
                'video',
                'spellCheckStatus',
                'spellCheckedText',
                'speechRecognitionDictionary',
              ]),
              (value) => value instanceof File
            ),
            videoStartTime: this.form.getValue('item.videoStartTime') || undefined,
            videoEndTime: this.form.getValue('item.videoEndTime') || undefined,
            type: this.form.getValue('item.type').id,
            grammar: this.form.getValue('item.grammar') && this.form.getValue('item.grammar').id,
            reference: this.form.getValue('item.reference') || undefined,
            text: sanitizeText(this.form.getValue('item.text')) || undefined,
            ...(Object.keys(sanitizeDictionary(this.form.getValue('item.speechRecognitionDictionary'))).length && {
              speechRecognitionDictionary: sanitizeDictionary(this.form.getValue('item.speechRecognitionDictionary')),
            }),
            ...(this.form.getValue('item.gaps') && {
              gaps: sanitizeGapFillDictationGaps(this.form.getValue('item.gaps')),
            }),
            ...(this.form.getValue('item.answers') && {
              answers: this.form
                .getValue('item.answers')
                .map((answer) =>
                  omitBy(
                    {
                      ...answer,
                      text: sanitizeText(answer.text),
                      ...(answer.id && !answer.id.match(ISO_DATE_PATTERN) && { _id: answer.id }),
                    },
                    (value) => value instanceof File
                  )
                ),
            }),
          }),
        ],
        { type: 'application/json' }
      )
    );

    formData.append('order', this.form.getValue('order') || this.order);
    formData.append('group', this.form.getValue('group') || 1);

    this.submit
      .fetch({
        method: itemId ? 'put' : 'post',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        url: itemId ? `/${this.endpointUrl}/${itemId}` : `/${this.endpointUrl}`,
        body: jsonFormData(
          {
            ...pickBy(omit(this.form.getValue('item'), ['video', 'spellCheckStatus', 'spellCheckedText']), (value) => value instanceof File),
            ...(this.form.getValue('item.answers') && {
              answers: this.form
                .getValue('item.answers')
                .map((answer) => pickBy(answer, (value) => value instanceof File)),
            }),
          },
          {
            initialFormData: formData,
            mapping: (value) => {
              if (typeof value === 'boolean') {
                return toJS(value);
              }
              return toJS(value);
            },
          }
        ),
      })
      .then(() => {
        if (this.submit.data) {
          this.submit
            .fetch({
              url: `/items/${this.submit.data.item.id || this.submit.data.item}`,
            })
            .then(async () => {
              if (this.submit.data) {
                if (this.form.getValue('item.video') && this.form.getValue('item.video') instanceof File) {
                  NotificationService.addNotification(
                    `Item ${itemId ? 'updated' : 'created'} and starting video upload`,
                    'info'
                  );
                  VideoUploadService.init();
                  try {
                    await new Promise((resolve, reject) => {
                      VideoUploadService.handleUpload(
                        this.form.getValue('item.video'),
                        this.submit.data.videoUploadLink
                      ).subscribe({
                        error: () => {
                          reject();
                        },
                        complete: () => {
                          resolve();
                        },
                      });
                    });
                    NotificationService.addNotification('Video uploaded successfully', 'success');
                  } catch (e) {
                    this.form.setInitialValues({
                      ...this.form.getValues(),
                      item: {
                        ...this.form.getValue('item'),
                        id: this.submit.data.id,
                      },
                    });
                    return;
                  }
                } else {
                  NotificationService.addNotification(
                    `Item ${itemId ? 'updated' : 'created'} successfully.`,
                    'success'
                  );
                }
                this.form.setInitialValues({
                  ...this.form.getValues(),
                  item: {
                    ...this.form.getValue('item'),
                    video: this.submit.data.videoLink,
                    videoFileSize: null,
                    ...pick(this.submit.data, [
                      'audio',
                      'image',
                      'videoLink',
                      'videoExternalLink',
                      'answers',
                      'nativeSpeechRecognition',
                    ]),
                  },
                });
                this.onSaveSuccess();
              }
            });
        }
        if (this.submit.error) {
          NotificationService.addNotification(`Error ${itemId ? 'updating' : 'creating'} item.`, 'error');
        }
      });
  });

  handleRefreshNativeSpeechRecognition = action(() => {
    const itemId = this.form.getValue('item.id');
    this.submit
      .fetch({
        method: 'patch',
        url: `/${this.endpointUrl}/${itemId}/generate-native-sr`,
      })
      .then(() => {
        if (this.submit.data) {
          this.form.setValue('item.nativeSpeechRecognition', toJS(this.submit.data.nativeSpeechRecognition));
          NotificationService.addNotification(
            `Native speech slices ${itemId ? 'updated' : 'created'} successfully.`,
            'success'
          );
        }
        if (this.submit.error) {
          NotificationService.addNotification(
            `Error ${itemId ? 'updating' : 'creating'} native speech slices.`,
            'error'
          );
        }
      });
  });

  handleGenerateAudios = action(() => {
    const itemId = this.form.getValue('item.id');
    this.submit
      .fetch({
        method: 'patch',
        url: `/${this.endpointUrl}/${itemId}/generate-audios`,
      })
      .then(() => {
        if (this.submit.data) {
          this.form.setValue('item.audio', toJS(this.submit.data.audio));
          this.form.setValue('item.postPhraseAudio', toJS(this.submit.data.postPhraseAudio));
          if (this.submit.data.answers) {
            this.form.setValue('item.answers', toJS(this.submit.data.answers));
          }
          NotificationService.addNotification(`Audios successfully generated.`, 'success');
        }
        if (this.submit.error) {
          NotificationService.addNotification(`Error generating audios.`, 'error');
        }
      });
  });

  handleGenerateImagePrompt = action(() => {
    const itemId = this.form.getValue('item.id');
    this.submit
      .fetch({
        method: 'patch',
        url: `/${this.endpointUrl}/${itemId}/generate-image-prompt`,
      })
      .then(() => {
        if (this.submit.data) {
          this.form.setValue('item.imagePrompt', toJS(this.submit.data.imagePrompt));
          this.form.setValue('item.postPhraseImagePrompt', toJS(this.submit.data.postPhraseImagePrompt));
          if (this.submit.data.answers) {
            this.form.setValue('item.answers', toJS(this.submit.data.answers));
          }
          NotificationService.addNotification(`Images successfully generated.`, 'success');
        }
        if (this.submit.error) {
          NotificationService.addNotification(`Error generating images.`, 'error');
        }
      });
  });

  handleGenerateImages = action(() => {
    const itemId = this.form.getValue('item.id');
    this.submit
      .fetch({
        method: 'patch',
        url: `/${this.endpointUrl}/${itemId}/generate-images`,
      })
      .then(() => {
        if (this.submit.data) {
          this.form.setValue('item.image', toJS(this.submit.data.image));
          this.form.setValue('item.postPhraseImage', toJS(this.submit.data.postPhraseImage));
          if (this.submit.data.answers) {
            this.form.setValue('item.answers', toJS(this.submit.data.answers));
          }
          NotificationService.addNotification(`Images successfully generated.`, 'success');
        }
        if (this.submit.error) {
          NotificationService.addNotification(`Error generating images.`, 'error');
        }
      });
  });

  handleGenerateTranslations = action(() => {
    const itemId = this.form.getValue('item.id');
    this.submit
      .fetch({
        method: 'patch',
        url: `/${this.endpointUrl}/${itemId}/generate-translations`,
      })
      .then(() => {
        if (this.submit.data) {
          this.form.setValue('item.translation', toJS(this.submit.data.translation));
          this.form.setValue('item.spanishTranslation', toJS(this.submit.data.spanishTranslation));
          NotificationService.addNotification(`Translations successfully generated.`, 'success');
        }
        if (this.submit.error) {
          NotificationService.addNotification(`Error generating translations.`, 'error');
        }
      });
  });
}
